// debug-chrome — the isolated "debug" surface (debug-activity [5.1], a console-Python
// "find & fix the bug" activity). Host chrome AROUND the unchanged runner: the teacher's
// buggy program loads in an EDITABLE editor (Ace, readOnly:false, like assignment) with
// the shared gold Run visible in the toolbar so the student experiments freely. A
// dedicated Check button runs the LIVE edited buffer, accumulates its stdout, and grades
// it against the shipped golden via challenge-grading.js `compareExpectedOutput` (the
// SAME grader as the Code Challenge expected-output checkpoint).
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
//
// The golden is SHIPPED in #debug-config (the client compare needs it; the same peeking
// precedent as predict / the challenge expected-output checkpoint) — the surface only
// ever DISPLAYS a first-differing-line diff, never the golden verbatim. On the first
// passing Check the solve is stored via `respond` ({ solved:true, attempts }). NO
// runner-contract change; editor-core untouched.

import { compareExpectedOutput } from './challenge-grading.js';
import { mountPreviewStrip } from './preview-strip.js';
// tiered-hints (Trello 6WQKXSWl): the shared three-tier hint ladder renderer.
import { renderLadder, buildLadder } from './hint-ladder.js';
// activity-retry-and-close-on-success (Trello 2eajTgm0): the close-on-success prompt.
import { offerClose, offerReturn } from './close-prompt.js';

// A student's edited buffer can contain an infinite loop (a common bug), so Check bounds
// the wall-clock like the challenge console check — on timeout we stop the run and show a
// friendly "didn't finish" not-yet (never a hang).
var CHECK_TIMEOUT_MS = 15000;

// Read + parse the server-embedded compare config (already '<'-escaped by the handler).
function readConfig(doc) {
  var el = doc.getElementById('debug-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    if (!cfg || typeof cfg !== 'object') { return null; }
    return cfg;
  } catch (e) { return null; }
}

export function wire(ctx) {
  var doc  = ctx.doc;
  var core = ctx.core;
  var cfg  = readConfig(doc);
  if (!cfg) { return; }

  // teacher-preview-split (tasks 9.1/9.3): SERVER-RESOLVED preview mode. `cfg.preview` was
  // set only after the route's pre-handler confirmed `isCourseStaff` (R17), so a student who
  // opens a shared or bookmarked preview-flagged URL sees no strip and keeps persisting
  // exactly as today.
  var previewMode = cfg.preview === true;
  // one-tab-same-tab-launches (Trello bD1oe35m): the activity top bar's preview chip and "Back to
  // class" replace the strip, so it is mounted only when the bar is absent (card 2's guard).
  if (!doc.getElementById('surface-topbar-back')) {
    mountPreviewStrip(doc, { active: previewMode, exit: 'reload' });
  }

  var panel = doc.getElementById('debug-panel');
  if (!panel) { return; }

  var normalize = (cfg.normalize && typeof cfg.normalize === 'object') ? cfg.normalize : {};
  var canManage = !!cfg.canManage;

  // ── surface state ─────────────────────────────────────────────────────────
  var ready     = false;   // runner ready (Check is a no-op / disabled before this)
  var checking  = false;   // a Check run is in flight
  var submitted = false;   // the solved response has been POSTed (once)
  var attempts  = 0;       // client-counted number of Checks (sent on the solving POST)

  var stdoutAcc  = '';     // accumulated STDOUT for THIS check run (reset per Check)
  var errorText  = '';     // stderr / error detail for this run
  var inputAsked = false;  // the program requested stdin (non-interactive guidance)
  var timedOut   = false;  // the run was stopped by the wall-clock timeout
  var timer      = null;   // the wall-clock timeout handle

  // ── DOM ───────────────────────────────────────────────────────────────────
  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }

  var heading = el('h2', 'debug-heading');
  var bug = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  bug.setAttribute('class', 'debug-icon'); bug.setAttribute('viewBox', '0 0 24 24');
  bug.setAttribute('width', '20'); bug.setAttribute('height', '20'); bug.setAttribute('fill', 'none');
  bug.setAttribute('stroke', 'currentColor'); bug.setAttribute('stroke-width', '2');
  bug.setAttribute('stroke-linecap', 'round'); bug.setAttribute('stroke-linejoin', 'round');
  bug.setAttribute('aria-hidden', 'true');
  bug.innerHTML = '<path d="m8 2 1.88 1.88"/><path d="M14.12 3.88 16 2"/>' +
    '<path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/>' +
    '<path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"/>' +
    '<path d="M12 20v-9"/><path d="M6.53 9C4.6 8.8 3 7.1 3 5"/><path d="M6 13H2"/>' +
    '<path d="M3 21c0-2.1 1.7-3.9 3.8-4"/><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"/>' +
    '<path d="M22 13h-4"/><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"/>';
  heading.appendChild(bug);
  heading.appendChild(doc.createTextNode('Find and fix the bug'));
  panel.appendChild(heading);
  panel.appendChild(el('p', 'debug-sub',
    'Change the code to fix the bug. Press Run to see what it prints, then press Check my fix to compare it with what it should print.'));

  // Actions.
  var actions = el('div', 'debug-actions');
  var checkBtn = el('button', 'debug-btn debug-btn-check', 'Check my fix');
  checkBtn.type = 'button';
  checkBtn.disabled = true;
  var hint = el('p', 'debug-hint', 'Loading runner…');
  actions.appendChild(checkBtn);
  actions.appendChild(hint);
  panel.appendChild(actions);

  if (canManage) {
    panel.appendChild(el('div', 'debug-preview-note',
      'Preview mode. Nothing you do here is saved.'));
  }

  // Result (hidden until the first Check finishes).
  var result = el('div', 'debug-result');
  result.hidden = true;
  var verdict = el('div', 'debug-verdict');
  var diffLabel = el('p', 'debug-tip-label', 'What to look at');
  var diff = el('pre', 'debug-diff');
  var tip = el('p', 'debug-tip', '');
  var saveStatus = el('p', 'debug-save-status', '');
  result.appendChild(verdict);
  result.appendChild(diffLabel);
  result.appendChild(diff);
  // The hint ladder is available from the start (a student can be stuck before ever pressing
  // Check), so it lives in the panel above the result rather than inside it. Built ONCE per page
  // load, so reveals are kept across Checks. A Hint-1-only activity gets a one-tier ladder.
  var ladderBox = null;
  if (buildLadder(cfg).length) {
    ladderBox = el('div', 'hl-debug');
    ladderBox.appendChild(renderLadder(doc, cfg, {
      renderText: function (target, text) { target.textContent = text; }
    }));
  }
  result.appendChild(tip);
  result.appendChild(saveStatus);
  if (ladderBox) { panel.appendChild(ladderBox); }
  panel.appendChild(result);

  // ── gating ────────────────────────────────────────────────────────────────
  function updateCheckEnabled() {
    // Check runs whenever the runner is ready and no Check is in flight. It stays
    // enabled after a solve so the student can keep experimenting (the solve is
    // sticky server-side — re-Checks are no-ops).
    checkBtn.disabled = !(ready && !checking);
  }

  function clearTimer() { if (timer) { clearTimeout(timer); timer = null; } }

  // ── Check ─────────────────────────────────────────────────────────────────
  checkBtn.addEventListener('click', function () {
    if (!ready || checking) { return; }
    checking = true;
    attempts += 1;
    // Reset the accumulator + flags so a prior manual Run's output can't leak in.
    stdoutAcc = ''; errorText = ''; inputAsked = false; timedOut = false;
    updateCheckEnabled();
    hint.textContent = 'Running your code…';
    clearTimer();
    // A non-terminating buffer (infinite loop) would never reach a terminal state —
    // the wall-clock timeout stops it and grades whatever it printed as a not-yet.
    timer = setTimeout(function () {
      if (!checking) { return; }
      timedOut = true;
      core.stop();
    }, CHECK_TIMEOUT_MS);
    core.run();
  });

  // ── run listeners ───────────────────────────────────────────────────────────
  core.on('ready', function () {
    ready = true;
    hint.textContent = 'Fix the code, then Check it.';
    updateCheckEnabled();
  });
  core.on('stdout', function (text) { if (checking) { stdoutAcc += String(text == null ? '' : text); } });
  core.on('stderr', function (text) { if (checking) { errorText += String(text == null ? '' : text); } });
  core.on('error', function (detail) {
    if (!checking) { return; }
    var d = detail && detail.detail ? detail.detail : detail;
    if (d != null) { errorText += String(d); }
  });
  // Debug targets NON-interactive console programs. If the buffer parks on input(),
  // editor-core exposes no programmatic stdin send, so stop() the parked run — it
  // terminates as `stopped`, which the state handler grades as a finish.
  core.on('input-request', function () {
    if (!checking) { return; }
    inputAsked = true;
    core.stop();
  });
  core.on('state', function (state) {
    if (!checking) { return; }
    if (state === 'finished' || state === 'stopped'
        || state === 'student-error' || state === 'runner-error') {
      checking = false;
      clearTimer();
      grade(state);
      updateCheckEnabled();
    }
  });

  // ── grade ─────────────────────────────────────────────────────────────────
  function grade(state) {
    // A timeout is a friendly not-yet — a non-terminating fix never grades green.
    if (timedOut) {
      showResult(false, "Your program didn't finish — is there a loop that never ends?");
      return;
    }

    // A runner/student error or a stdin park counts as a normal not-yet: grade the
    // accumulated stdout as the actual output (never throw). The grader's diff will
    // show the first line that differs from the golden.
    var res = compareExpectedOutput({
      goldenOutput: cfg.expectedOutput,
      normalize: normalize,
      label: 'Program output'
    }, stdoutAcc);

    var supplementalTip = '';
    if (res.passed && (state === 'student-error' || state === 'runner-error' || inputAsked)) {
      // Defensive: a "pass" while the program also errored is unusual, but the golden
      // compare is authoritative. No extra tip needed.
      supplementalTip = '';
    }
    else if (!res.passed) {
      if (state === 'student-error' || errorText) {
        supplementalTip = 'Your program hit an error before it finished — check the console below the code.';
      }
      else if (inputAsked) {
        supplementalTip = 'This debugging challenge grades printed output — it works best with programs that do not ask for keyboard input.';
      }
    }

    showResult(res.passed, res.diff, supplementalTip);
  }

  function showResult(passed, diffText, supplementalTip) {
    verdict.classList.remove('is-fixed', 'is-notyet');
    if (passed) {
      verdict.classList.add('is-fixed');
      verdict.textContent = '✓ Fixed!';
      diffLabel.hidden = true;
      diff.hidden = true;
      diff.textContent = '';
    }
    else {
      verdict.classList.add('is-notyet');
      verdict.textContent = '✗ Not quite yet';
      diffLabel.hidden = !diffText;
      diff.hidden = !diffText;
      diff.textContent = diffText || '';
    }

    // A pass shows the success text; a miss shows only the runtime note when relevant (the
    // teacher's hints live in the always-available ladder above).
    var hintText = passed
      ? 'Nice work — your output matches what it should print.'
      : (supplementalTip || '');
    tip.hidden = !hintText;
    tip.textContent = hintText;

    result.hidden = false;
    hint.textContent = passed ? '' : 'Keep going — fix the code and Check again.';

    if (passed) { submitSolve(); }
  }

  // ── store the solve (once) ───────────────────────────────────────────────────
  function submitSolve() {
    if (submitted) { return; }
    submitted = true;
    // teacher-preview-split (task 9.2 / D7): CLIENT-graded — this call only STORES. In
    // server-confirmed preview mode nothing may be written, so skip the store entirely; the
    // Check feedback above is identical to what a student sees.
    if (previewMode) { saveStatus.textContent = 'Preview — nothing is saved.'; return; }
    var url = '/api/courses/' + cfg.courseId + '/lessons/' + cfg.lessonId
            + '/materials/' + cfg.materialId + '/respond';
    try {
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        // per-class-material-progress: stamp the class cohort key (launch URL → cfg) when present.
        body: JSON.stringify(cfg.linkedCourseId
          ? { answers: { solved: true, attempts: attempts }, linkedCourseId: cfg.linkedCourseId }
          : { answers: { solved: true, attempts: attempts } })
      }).then(function (res) {
        // A teacher preview (canManage) is 403'd by the roster course-student guard —
        // expected and silent; nothing is stored.
        if (res && res.ok && !canManage) { saveStatus.textContent = 'Saved to your progress.'; }
        // activity-retry-and-close-on-success: offer the way back only AFTER the store has
        // settled. Closing the tab with this request in flight aborts it and records nothing.
        offerAfterSolve(!!(res && res.ok));
      }).catch(function () { offerAfterSolve(false); });
    } catch (e) { offerAfterSolve(false); }
  }

  // activity-retry-and-close-on-success: never for a teacher (debug carries `cfg.canManage`),
  // and never over a rejected save — a countdown that closes the tab on unsaved work is the
  // exact failure this prompt exists to avoid, so that case gets a plain way back and a note.
  // debug opens in its OWN tab, so its ending is a close with a navigate-free fallback.
  function offerAfterSolve(saved) {
    if (previewMode || canManage) { return; }
    if (!saved) {
      offerReturn({ sameTab: true, warn: true, label: 'We couldn’t save that. Please tell your teacher.' });
      return;
    }
    offerClose({ sameTab: true, label: 'Nice work — you fixed the bug.' });
  }

  // The runner may already be ready by the time the chrome wires up; reflect that.
  updateCheckEnabled();
}
